import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './VideoInterview.module.css';

interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  sessionId: string | null;
}

interface WebSocketMessage {
  event: string;
  data: any;
}

const VideoInterview: React.FC = () => {
  const { userId, projectId, episodeId } = useParams<{
    userId: string;
    projectId: string;
    episodeId?: string;
  }>();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { completeCameraRecording, getCameraRecordingWebSocketUrl } = useApi();
  
  // 상태 관리
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    sessionId: null
  });
  
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  
  
  // 녹화 중지
  const handleStopRecording = useCallback(async () => {
    try {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      
      if (websocketRef.current) {
        websocketRef.current.send(JSON.stringify({ command: 'stop' }));
      }
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isPaused: false
      }));
      
      // 녹화 완료 처리
      if (recordingState.sessionId && projectId) {
        try {
          if (episodeId) {
            // 에피소드 레벨 녹화
            await completeCameraRecording(parseInt(projectId), recordingState.sessionId, parseInt(episodeId));
          } else {
            // 프로젝트 레벨 녹화 (episodeId 없이 호출)
            await completeCameraRecording(parseInt(projectId), recordingState.sessionId);
          }
          console.log('녹화 완료 처리 성공');
        } catch (err) {
          console.error('녹화 완료 처리 실패:', err);
          setError('녹화 저장에 실패했습니다.');
        }
      }
      
      // 웹소켓 연결 종료
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
        console.log('웹소켓 연결이 종료되었습니다.');
      }
      
    } catch (err) {
      console.error('녹화 중지 실패:', err);
      setError('녹화 중지에 실패했습니다.');
      
      // 에러 발생 시에도 웹소켓 연결 종료
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
        console.log('에러 발생으로 웹소켓 연결이 종료되었습니다.');
      }
    }
  }, [recordingState.sessionId, projectId, episodeId, completeCameraRecording]);

  
  // 웹소켓 연결 함수
  const connectWebSocket = useCallback(async (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        let wsUrl = episodeId 
            ? getCameraRecordingWebSocketUrl(parseInt(projectId || ""), parseInt(episodeId || ""))
            : getCameraRecordingWebSocketUrl(parseInt(projectId || ""));

        if (!wsUrl) {
          setError('WebSocket URL을 생성할 수 없습니다.');
          reject(new Error('WebSocket URL을 생성할 수 없습니다.'));
          return;
        }

        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('WebSocket 연결 성공');
          websocketRef.current = ws;
          resolve(true);
        };
        
        ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            console.log('WebSocket 메시지 수신:', message);
            
            if (message.event === 'session-start') {
              setRecordingState(prev => ({
                ...prev,
                sessionId: message.data.session_id
              }));
            } else if (message.event === 'recording-status') {
              console.log('녹화 상태:', message.data.status);
            } else if (message.event === 'session-timeout') {
              setError('세션이 타임아웃되었습니다. 다시 시도해주세요.');
              // 상태를 통해 녹화 중지 신호 전달
              setRecordingState(prev => ({
                ...prev,
                isRecording: false,
                isPaused: false
              }));
            }
          } catch (err) {
            console.error('WebSocket 메시지 파싱 오류:', err);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket 오류:', error);
          setError('서버 연결에 실패했습니다.');
          reject(error);
        };
        
        ws.onclose = () => {
          console.log('WebSocket 연결 종료');
          websocketRef.current = null;
        };
        
      } catch (err) {
        console.error('WebSocket 연결 실패:', err);
        setError('서버 연결에 실패했습니다.');
        reject(err);
      }
    });
  }, [projectId, episodeId, getCameraRecordingWebSocketUrl]);

  // 녹화 시작
  const handleStartRecording = useCallback(async () => {
    if (!streamRef.current) {
      setError('카메라가 필요합니다.');
      return;
    }
    
    try {
      // 웹소켓 연결
      await connectWebSocket();
      
      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        setError('서버 연결이 완료되지 않았습니다.');
        return;
      }
      
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          
          // WebSocket으로 영상 데이터 전송
          if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
            websocketRef.current.send(event.data);
          }
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log('녹화 중지됨');
      };
      
      mediaRecorder.start(1000); // 1초마다 데이터 전송
      
      // 녹화 제어 명령 전송
      if (websocketRef.current) {
        websocketRef.current.send(JSON.stringify({ command: 'start' }));
      }
      
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false
      }));
      
      // 타이머 시작
      const timer = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }));
      }, 1000);
      recordingTimerRef.current = timer;
      
    } catch (err) {
      console.error('녹화 시작 실패:', err);
      setError('녹화를 시작할 수 없습니다.');
    }
  }, [connectWebSocket]);
  
  // 녹화 일시정지/재개
  const handleTogglePause = useCallback(() => {
    if (!mediaRecorderRef.current || !websocketRef.current) return;
    
    const newPausedState = !recordingState.isPaused;
    
    if (newPausedState) {
      mediaRecorderRef.current.pause();
      websocketRef.current.send(JSON.stringify({ command: 'pause' }));
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    } else {
      mediaRecorderRef.current.resume();
      websocketRef.current.send(JSON.stringify({ command: 'resume' }));
      const timer = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }));
      }, 1000);
      recordingTimerRef.current = timer;
    }
    
    setRecordingState(prev => ({
      ...prev,
      isPaused: newPausedState
    }));
  }, [recordingState.isPaused]);
  
  // 컴포넌트 마운트 시 카메라 초기화
  useEffect(() => {
    const initializeCameraInternal = async () => {
      try {
        setError(null);
        setIsConnecting(true);
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          },
          audio: true
        });
        
        streamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        
        setCameraPermission(true);
        setIsConnecting(false);
      } catch (err) {
        console.error('카메라 초기화 실패:', err);
        setError('카메라 접근 권한이 필요합니다. 브라우저 설정에서 카메라 권한을 허용해주세요.');
        setCameraPermission(false);
        setIsConnecting(false);
      }
    };

    initializeCameraInternal();
    
    return () => {
      // 정리 작업
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);
  
  // 시간 포맷팅
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    if (recordingState.isRecording) {
      if (window.confirm('녹화 중입니다. 정말 나가시겠습니까?')) {
        handleStopRecording();
        navigateBack();
      }
    } else {
      navigateBack();
    }
  };

  const navigateBack = () => {
    console.log('handleGoBack called');
    console.log('fromPage:', fromPage);
    console.log('userId:', userId);
    
    let targetPath: string;
    
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      console.log('Navigating to fromPage:', fromPage);
      targetPath = fromPage;
    } else {
      console.log('Navigating to user detail:', `/admin/users/detail/${userId}`);
      targetPath = `/admin/users/detail/${userId}`;
    }
    
    try {
      navigate(targetPath);
      console.log('navigate function called successfully');
    } catch (error) {
      console.error('navigate function failed:', error);
      console.log('Falling back to window.location.href');
      window.location.href = targetPath;
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };
  
  return (
    <div className={styles.videoInterviewContainer}>
      {/* 페이지 헤더 */}
      <div className={styles.pageTitleRow}>
        <h1>🎥 {episodeId ? '에피소드 영상 인터뷰' : '프로젝트 레벨 영상 인터뷰'}</h1>
        <button 
          onClick={handleGoBack} 
          className={styles.btnBack}
        >
          {getBackButtonText()}
        </button>
      </div>

      {/* 인터뷰 정보 섹션 */}
      <div className={styles.userInfoSection}>
        <div className={styles.sectionHeader}>
          <h2>📹 인터뷰 세션</h2>
          <div className={styles.sectionHeaderActions}>
            <span className={styles.statusBadge + ' ' + styles.default}>
              프로젝트: {projectId}{episodeId ? ` | 에피소드: ${episodeId}` : ' (프로젝트 레벨)'}
            </span>
          </div>
        </div>
      </div>
      
      {error && (
        <div className={styles.errorMessage}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {error}
        </div>
      )}
      
      {/* 비디오 인터뷰 메인 콘텐츠 */}
      <div className={styles.userInfoSection}>
        <div className={styles.sectionHeader}>
          <h2>📹 비디오 인터뷰</h2>
        </div>
        <div className={styles.mainContent}>
        <div className={styles.videoSection}>
          <div className={styles.videoContainer}>
            <video
              ref={videoRef}
              className={styles.videoPreview}
              autoPlay
              muted
              playsInline
            />
            
            {isConnecting && (
              <div className={styles.loadingOverlay}>
                <div className={styles.spinner}></div>
                <p>카메라 연결 중...</p>
              </div>
            )}
            
            {cameraPermission === false && (
              <div className={styles.errorOverlay}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 10L11 14L9 12M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>카메라 접근 권한이 필요합니다</p>
                <button onClick={() => window.location.reload()} className={styles.retryButton}>
                  다시 시도
                </button>
              </div>
            )}
            
            {recordingState.isRecording && (
              <div className={styles.recordingIndicator}>
                <div className={styles.recordingDot}></div>
                <span>REC</span>
              </div>
            )}
          </div>
          
          <div className={styles.recordingInfo}>
            <div className={styles.timer}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{formatTime(recordingState.duration)}</span>
            </div>
            
            {recordingState.sessionId && (
              <div className={styles.sessionInfo}>
                <span>세션: {recordingState.sessionId}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.controlsSection}>
          <div className={styles.controlButtons}>
            {!recordingState.isRecording ? (
              <button
                onClick={handleStartRecording}
                disabled={!cameraPermission || isConnecting}
                className={`${styles.controlButton} ${styles.startButton}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                </svg>
                녹화 시작
              </button>
            ) : (
              <>
                <button
                  onClick={handleTogglePause}
                  className={`${styles.controlButton} ${styles.pauseButton}`}
                >
                  {recordingState.isPaused ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="currentColor"/>
                    </svg>
                  )}
                  {recordingState.isPaused ? '재개' : '일시정지'}
                </button>
                
                <button
                  onClick={handleStopRecording}
                  className={`${styles.controlButton} ${styles.stopButton}`}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="6" width="12" height="12" fill="currentColor"/>
                  </svg>
                  녹화 중지
                </button>
              </>
            )}
          </div>
          
          <div className={styles.statusInfo}>
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>카메라 상태:</span>
              <span className={`${styles.statusValue} ${cameraPermission ? styles.success : styles.error}`}>
                {cameraPermission ? '연결됨' : '연결 안됨'}
              </span>
            </div>
            
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>녹화 상태:</span>
              <span className={`${styles.statusValue} ${recordingState.isRecording ? styles.recording : styles.idle}`}>
                {recordingState.isRecording 
                  ? (recordingState.isPaused ? '일시정지' : '녹화 중') 
                  : '대기 중'
                }
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default VideoInterview;
